
import { useMemo } from 'react'
import type { AINodeType, AiModelPurpose } from '@genieartinc/schemas'
import { useModelWithOverrides } from '@/hooks/useModelWithOverrides'
import type { MergedParameter } from '@/lib/ai/parameter-merge'

export interface UsePinnedParametersParams {
  pinnedNames: string[]
  nodeType: AINodeType
  modelFamilySlug?: string
  modelId?: string
  purpose?: AiModelPurpose
  settings?: Record<string, unknown>
}

export interface UsePinnedParametersResult {
  parameters: MergedParameter[]
  values: Record<string, unknown>
}

/**
 * usePinnedParameters
 *
 * Returns merged parameter definitions and current values for pinned parameters.
 * Uses the same parameter merge pipeline as the settings panel to ensure consistency.
 *
 * Only fetches model data when there are pinned parameters to display.
 */
export function usePinnedParameters({
  pinnedNames,
  nodeType,
  modelFamilySlug,
  modelId,
  purpose,
  settings,
}: UsePinnedParametersParams): UsePinnedParametersResult {
  // Nodes without pins do not need a second model/schema request.
  const { settingsParams } = useModelWithOverrides(
    nodeType,
    modelFamilySlug,
    modelId || '',
    purpose,
    undefined,
    Boolean(modelId) && pinnedNames.length > 0,
  )

  // Filter to only pinned parameters, preserving pinned order
  const parameters = useMemo(() => {
    if (pinnedNames.length === 0 || settingsParams.length === 0) return []

    const paramsByName = new Map<string, MergedParameter>()
    for (const param of settingsParams) {
      paramsByName.set(param.name, param)
    }

    // Return in pinned order, filtering out any that don't exist in the model
    const result: MergedParameter[] = []
    for (const name of pinnedNames) {
      const param = paramsByName.get(name)
      if (param) {
        result.push(param)
      }
    }
    return result
  }, [pinnedNames, settingsParams])

  // Extract current values from settings for pinned parameters
  const values = useMemo(() => {
    if (pinnedNames.length === 0) return {}

    const result: Record<string, unknown> = {}
    for (const param of parameters) {
      const settingsValue = settings?.[param.name]
      result[param.name] = settingsValue !== undefined ? settingsValue : param.default
    }
    return result
  }, [pinnedNames, parameters, settings])

  return { parameters, values }
}
